import { Menu, Search } from 'lucide-react';
import { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { ChatsRealtimeSync } from '@/features/chats/realtime-sync';
import { useCan, useUser } from '@/lib/auth';
import { PrefsProvider } from '@/lib/prefs';
import { RealtimeProvider, useRealtime } from '@/lib/realtime';
import { cn } from '@/lib/utils';
import { Keys } from '@/components/ui/keys';
import { CommandPalette } from '../command-palette';
import { FullPageSpinner } from '../route-guards';
import { ShortcutsDialog } from '../shortcuts-dialog';
import { Brand, SidebarNav } from './sidebar';
import { UserMenu } from './user-menu';

function ConnectionDot() {
  const { socket, connected } = useRealtime();
  if (!socket) return null;
  return (
    <span
      className="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex"
      title={connected ? 'Live updates connected' : 'Reconnecting…'}
    >
      <span
        className={cn('size-2 rounded-full', connected ? 'bg-success' : 'animate-pulse bg-warning')}
      />
      {connected ? 'Live' : 'Reconnecting'}
    </span>
  );
}

export function AppShell() {
  const can = useCan();
  const user = useUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  // Chats is a full-height three-pane layout; everything else scrolls the main area.
  const fullBleed = pathname.startsWith('/chats');

  return (
    <PrefsProvider key={user.id} userId={user.id}>
      <RealtimeProvider key={user.id} enabled={can('chats:handle')}>
        <ChatsRealtimeSync />
        <CommandPalette />
        <ShortcutsDialog />
        <div className="flex h-dvh overflow-hidden">
          <aside className="hidden w-60 shrink-0 flex-col border-r bg-sidebar lg:flex">
            <Brand />
            <div className="min-h-0 flex-1 overflow-y-auto">
              <SidebarNav />
            </div>
          </aside>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent
              className="right-auto left-0 w-64 max-w-[80vw] border-r border-l-0 bg-sidebar"
              aria-describedby={undefined}
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <Brand />
              <div className="min-h-0 flex-1 overflow-y-auto">
                <SidebarNav onNavigate={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-card px-3 lg:px-4">
              <Button
                variant="ghost"
                size="icon-sm"
                className="lg:hidden"
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation"
              >
                <Menu />
              </Button>
              <SearchTrigger />
              <div className="flex-1" />
              <ConnectionDot />
              <UserMenu />
            </header>
            <main
              id="main"
              className={cn(
                'relative min-h-0 flex-1',
                fullBleed ? 'overflow-hidden' : 'overflow-y-auto',
              )}
            >
              <Suspense fallback={<FullPageSpinner />}>
                <Outlet />
              </Suspense>
            </main>
          </div>
        </div>
      </RealtimeProvider>
    </PrefsProvider>
  );
}

/** Visible entry point to the palette, so the shortcut is discoverable (rules 1 and 3). */
const openPalette = () =>
  window.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, metaKey: true, bubbles: true }),
  );

function SearchTrigger() {
  return (
    <button
      type="button"
      onClick={openPalette}
      className="flex h-8 w-full min-w-0 max-w-80 items-center gap-2 rounded-md border bg-background px-2.5 text-ui whitespace-nowrap text-muted-foreground transition-colors duration-fast hover:bg-accent"
    >
      <Search className="size-4" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-left">Search citizens, chats, pages…</span>
      <Keys combo="mod+k" className="hidden sm:inline-flex" />
    </button>
  );
}
